import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid';

import BackflowLogFormCommentOnly from './BackflowLogFormCommentOnly';
import BackflowLogFormFull from './BackflowLogFormFull';
import BackflowLogSubmitButtons from './BackflowLogSubmitButtons';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import OutOfServiceCheckbox from './OutOfServiceCheckbox';

import { BackflowLog, BackflowJobItem } from '../../model';
import axios from '../../../sharedModules/utils/axios.utils';
import { loadCachedLog } from '../../utils/backflowCache';

interface BackflowLogSectionProps {
  backflowJobItem: BackflowJobItem;
  jobStarted: boolean;
  fetchBackflowJobItem: () => void;
  onSavedOffline?: () => void;
}

const outOfServiceStatuses = ['out_of_service', 'hidden'];

const BackflowLogSection: React.FC<BackflowLogSectionProps> = ({
  backflowJobItem,
  jobStarted,
  fetchBackflowJobItem,
  onSavedOffline,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [formValues, setFormValues] = useState<BackflowLog>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [outOfService, setOutOfService] = useState<boolean>(false);
  const [requiredDeviceImageAttached, setRequiredDeviceImageAttached] = useState(false);
  const [requiredDataplateAttached, setRequiredDataplateAttached] = useState(false);
  const [showRequiredImageErrors, setShowRequiredImageErrors] = useState(false);

  const location = useLocation();
  const offlineOnly = new URLSearchParams(location.search).get('offlineOnly') === 'true';

  const fetchBackflowLog = async () => {
    setLoading(true);
    try {
      const response = await axios.get<any>(`/backflows/backflow_logs/${backflowJobItem.id}`);
      setFormValues(response.data.result || {});
      setErrorMessage('');
    } catch (error: any) {
      const isNetworkError = !navigator.onLine || error.code === 'ERR_NETWORK' || error.message === 'Network Error';

      if (isNetworkError) {
        const cached = loadCachedLog(backflowJobItem?.backflow_job_group_id, backflowJobItem?.id);
        if (cached) {
          setFormValues(cached.formValues);
          setOutOfService(cached.outOfService);
        } else {
          setFormValues({});
        }
        setErrorMessage('');
      } else {
        setErrorMessage('Something went wrong fetching the Backflow Log');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!backflowJobItem?.id) return;

    if (offlineOnly) {
      const cached = loadCachedLog(backflowJobItem?.backflow_job_group_id, backflowJobItem?.id);
      if (cached) {
        setFormValues(cached.formValues);
        setOutOfService(cached.outOfService);
      } else {
        setFormValues({});
      }
      setLoading(false);
    } else {
      fetchBackflowLog();
    }
  }, [backflowJobItem?.id, offlineOnly]);

  useEffect(() => {
    if (outOfServiceStatuses.includes(backflowJobItem?.status)) {
      setOutOfService(true);
    }
  }, [backflowJobItem?.status]);

  useEffect(() => {
    if (requiredDeviceImageAttached && requiredDataplateAttached) setShowRequiredImageErrors(false);
  }, [requiredDeviceImageAttached, requiredDataplateAttached]);

  const showFullForm = jobStarted && !outOfService;
  const showSubmitButtons = jobStarted || outOfService;

  return (
    <Grid container>
      <LoadingWrapper isLoading={loading}>
        <Grid container>
          <Grid item xs={12}>
            <ErrorMessage message={errorMessage} />
          </Grid>

          <Grid item xs={12}>
            <OutOfServiceCheckbox outOfService={outOfService} setOutOfService={setOutOfService} />
          </Grid>

          {outOfService && (
            <Grid item xs={12}>
              <BackflowLogFormCommentOnly formValues={formValues} setFormValues={setFormValues} />
            </Grid>
          )}

          {showFullForm && (
            <Grid item xs={12}>
              <BackflowLogFormFull
                backflowJobItem={backflowJobItem}
                formValues={formValues}
                setFormValues={setFormValues}
                offlineOnly={offlineOnly}
                onRequiredImagesValidityChange={(deviceImageAttached, dataplateAttached) => {
                  setRequiredDeviceImageAttached(deviceImageAttached);
                  setRequiredDataplateAttached(dataplateAttached);
                }}
                showRequiredImageErrors={showRequiredImageErrors}
              />
            </Grid>
          )}

          {showSubmitButtons && (
            <Grid item xs={12} style={{ marginTop: 15 }}>
              <BackflowLogSubmitButtons
                backflowJobItem={backflowJobItem}
                formValues={formValues}
                outOfService={outOfService}
                fetchBackflowJobItem={fetchBackflowJobItem}
                requiredDeviceImageAttached={showFullForm ? requiredDeviceImageAttached : true}
                requiredDataplateAttached={showFullForm ? requiredDataplateAttached : true}
                onRequiredImagesValidationFailed={() => setShowRequiredImageErrors(true)}
                onSavedOffline={onSavedOffline}
              />
            </Grid>
          )}
        </Grid>
      </LoadingWrapper>
    </Grid>
  );
};

export default BackflowLogSection;
